/**
 * Hook for managing export functionality (PDF, PNG, CSV)
 * @module wellRegistrationTotals/hooks/useExportHandlers
 */

import { useCallback, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { landscapePrintStyles, portraitPrintStyles } from "../styled";
import { DRILLDOWN_CSV_HEADERS } from "../constants";
import { formatDrilldownTitle, prepareDrilldownCsvRows } from "../utils";

/**
 * Manages print and PNG export handlers
 * @param {Object} params
 * @param {React.RefObject} params.mainTableRef - Ref to main table element
 * @param {React.RefObject} params.drilldownRef - Ref to drilldown element
 * @param {number} params.selectedYear - Currently selected year
 * @param {Object|null} params.selectedRow - Currently selected row
 * @param {Array} params.drilldownWells - Wells data for drilldown
 * @param {Function} params.prepareCsvData - Function to prepare combined report CSV
 * @returns {Object} Export handlers and data
 */
export const useExportHandlers = ({
  mainTableRef,
  drilldownRef,
  selectedYear,
  selectedRow,
  drilldownWells,
  prepareCsvData,
}) => {
  const drilldownTitle = selectedRow
    ? formatDrilldownTitle(selectedRow.row_label)
    : "";

  /**
   * Main table print handler (landscape orientation)
   */
  const handlePrintMain = useReactToPrint({
    content: () => mainTableRef.current,
    documentTitle: `Well Registration Totals - ${selectedYear}`,
    pageStyle: landscapePrintStyles,
  });

  /**
   * Drilldown print handler (portrait orientation)
   */
  const handlePrintDrilldown = useReactToPrint({
    content: () => drilldownRef.current,
    documentTitle: drilldownTitle || "Registered Wells",
    pageStyle: portraitPrintStyles,
  });

  /**
   * Export main table as PNG
   */
  const handlePngExportMain = useCallback(async () => {
    if (!mainTableRef.current) return;

    try {
      // Temporarily hide selected row styling for clean export
      const selectedEl = mainTableRef.current.querySelector(".selected-row");
      if (selectedEl) {
        selectedEl.classList.remove("selected-row");
        selectedEl.classList.add("selected-row-hidden");
      }

      const canvas = await html2canvas(mainTableRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      // Restore selected row styling
      if (selectedEl) {
        selectedEl.classList.remove("selected-row-hidden");
        selectedEl.classList.add("selected-row");
      }

      const link = document.createElement("a");
      link.download = `well-registration-totals-${selectedYear}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("Error generating PNG:", err);
    }
  }, [mainTableRef, selectedYear]);

  /**
   * Export drilldown panel as PNG
   */
  const handlePngExportDrilldown = useCallback(async () => {
    if (!drilldownRef.current) return;

    try {
      const canvas = await html2canvas(drilldownRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const link = document.createElement("a");
      const filename =
        selectedRow?.row_label?.replace(/\s+/g, "-") || "drilldown";
      link.download = `registered-wells-${filename}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("Error generating PNG:", err);
    }
  }, [drilldownRef, selectedRow]);

  /**
   * Prepare combined CSV data (main report with adjustments + drilldown if selected)
   */
  const prepareCombinedCsvData = useCallback(() => {
    const mainData = prepareCsvData();

    // If no drilldown selected, return main data (which already includes adjustments)
    if (!selectedRow || !drilldownWells || drilldownWells.length === 0) {
      return mainData;
    }

    // Add drilldown data with separator
    const separator = [[""], [""], [drilldownTitle]];
    const drilldownRows = prepareDrilldownCsvRows(
      drilldownWells,
      DRILLDOWN_CSV_HEADERS
    );

    return [...mainData, ...separator, ...drilldownRows];
  }, [prepareCsvData, selectedRow, drilldownWells, drilldownTitle]);

  /**
   * Prepare drilldown-only CSV data
   */
  const drilldownCsvData = useMemo(
    () => prepareDrilldownCsvRows(drilldownWells, DRILLDOWN_CSV_HEADERS),
    [drilldownWells]
  );

  return {
    drilldownTitle,
    handlePrintMain,
    handlePrintDrilldown,
    handlePngExportMain,
    handlePngExportDrilldown,
    prepareCombinedCsvData,
    drilldownCsvData,
  };
};
